import { Component, DestroyRef, Input, type OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, type FormGroup } from '@angular/forms';

import { type Country } from '../../../modules/master-data/countries/country.model';
import { CountryService } from '../../../modules/master-data/countries/country.service';
import { type State } from '../../../modules/master-data/states/state.model';
import { StateService } from '../../../modules/master-data/states/state.service';
import { SharedService } from '../../services/shared.service';

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
  standalone: false
})
export class AddressFormComponent implements OnInit {
  countryService = inject(CountryService);
  stateService = inject(StateService);
  sharedService = inject(SharedService);
  private destroyRef = inject(DestroyRef);

  @Input() parentForm!: FormGroup;
  @Input() viewMode = false;
  @Input() showActive = true;
  @Input() createMode = false;

  form!: FormGroup;

  countries: Country[] = [];
  filteredCountries = signal<Country[]>([]);
  countryFilterCtrl = new FormControl();

  states: State[] = [];
  filteredStates = signal<State[]>([]);
  stateFilterCtrl = new FormControl();

  ngOnInit(): void {
    this.form = this.parentForm;

    this.loadCountries();
    this.setupFilters();
  }

  loadCountries(): void {
    this.countryService.getCountriesForDropdown().subscribe(countries => {
      this.countries = countries;
      this.filteredCountries.set(countries);

      this.handleCountryChanges();

      const countryId = this.form.get('state.country.id')?.value;

      if (countryId) {
        this.loadStatesForCurrentCountry(countryId);
      } else if (countries.length === 1) {
        this.form.get('state.country.id')?.setValue(countries[0].id);
        this.form.get('state.country.name')?.setValue(countries[0].name);
      }
    });
  }

  handleCountryChanges(): void {
    this.form
      .get('state.country.id')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(id => {
        this.loadStatesForCurrentCountry(id);
      });
  }

  onCountryChange(): void {
    this.form.get('state.id')?.setValue(null, { emitEvent: false });
    this.states = [];
    this.filteredStates.set([]);
  }

  private loadStatesForCurrentCountry(countryId: string): void {
    if (!countryId) {
      return;
    }

    const selectedStateId = this.form.get('state.id')?.value;

    this.stateService.getStatesForDropdownByCountry(countryId).subscribe(states => {
      this.states = states;
      this.filteredStates.set(states);

      if (selectedStateId) {
        this.form.get('state.id')?.setValue(selectedStateId);
      }
    });
  }

  setupFilters(): void {
    this.countryFilterCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(term => {
      this.filteredCountries.set(this.countries.filter(c => c.name.toLowerCase().includes(term.toLowerCase())));
    });

    this.stateFilterCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(term => {
      this.filteredStates.set(this.states.filter(s => s.name.toLowerCase().includes(term.toLowerCase())));
    });
  }
}
